/**
 * Shared panel styling for the admin surface.
 *
 * One place so the tables, detail sections and hero headers can't drift apart.
 * Shadows come from theme/colors.js `shadow`, which is teal-tinted to match
 * the brand rather than a neutral grey drop.
 */

import { border, bg, shadow, easing, text } from '../../theme/colors';

const RADIUS = 2.5; // 20px at MUI's 8px base — softer than the old 16px

/** Standard panel: tables, detail sections. */
export const panelSx = {
  bgcolor: bg.paper,
  borderRadius: RADIUS,
  border: `1px solid ${border.light}`,
  boxShadow: shadow.sm,
  overflow: 'hidden',
};

/** The hero/identity panel at the top of a detail page — sits a little higher. */
export const heroPanelSx = {
  ...panelSx,
  boxShadow: shadow.md,
};

/** A panel that responds to the pointer — use only where the whole card is a target. */
export const interactivePanelSx = {
  ...panelSx,
  transition: `box-shadow 200ms ${easing.smooth}, transform 200ms ${easing.smooth}`,
  '&:hover': {
    boxShadow: shadow.md,
  },
};

/**
 * Header strip inside a panel. bg.header is deliberately a step darker than
 * bg.subtle: against the near-white admin ground (bg.pageLight) a slate-100
 * fill was indistinguishable from the page itself.
 */
export const panelHeaderSx = {
  px: 2.5,
  py: 1.75,
  bgcolor: bg.header,
  borderBottom: `1px solid ${border.light}`,
};

/** Table styling: readable row separators, no dangling last border. */
export const tableSx = {
  '& .MuiTableCell-root': { borderBottom: `1px solid ${border.light}` },
  '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
    borderBottom: 'none',
  },
  // Sticky-feeling header without position tricks: a touch more weight.
  '& .MuiTableHead-root .MuiTableCell-root': {
    bgcolor: bg.header,
    fontWeight: 600,
    color: text.secondary,
    letterSpacing: 0.2,
    fontSize: 12,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
};

/** Row hover that reads as a target without shifting layout. */
export const tableRowSx = {
  transition: `background-color 140ms ${easing.smooth}`,
  '&:hover': { bgcolor: bg.selected },
};

export default panelSx;
